import {Database} from '@tnc/shared';

import {dataTableActionT, dataTableColumnT} from '@/lib/types/dataTable.types';
import {ColumnSelectionT, SelectedColumnsKeysT} from '@/lib/types/query.types';

import {DataTablePropsT} from '@/components/dataTable';

/**
 * Creates an array of data table columns.
 *
 * @template T - The table name key from the Database.
 * @template RowType - The type of the row object for the specified table.
 * @param {dataTableColumnT<T, RowType>[]} columns - The columns to be included in the data table.
 * @returns {dataTableColumnT<T, RowType>[]} The array of data table columns.
 */
export function createDataTableColumns<
  T extends keyof Database['public']['Tables'],
  RowType extends object = Database['public']['Tables'][T]['Row'],
>(columns: dataTableColumnT<T, RowType>[]): dataTableColumnT<T, RowType>[] {
  return columns;
}

/**
 * Creates an array of data table actions.
 *
 * @template T - The table name key from the Database.
 * @template RowType - The type of the row object for the specified table.
 * @param {dataTableActionT<T, RowType>[]} actions - The actions to be included in the data table.
 * @returns {dataTableActionT<T, RowType>[]} The array of data table actions.
 */
export function createDataTableActions<
  T extends keyof Database['public']['Tables'],
  RowType extends object = Database['public']['Tables'][T]['Row'],
>(actions: dataTableActionT<T, RowType>[]): dataTableActionT<T, RowType>[] {
  return actions;
}

// Use the imported DataTablePropsT instead of trying to derive it
export type DataTableConfigPropsT<
  T extends keyof Database['public']['Tables'],
  C extends ColumnSelectionT<T>[],
> = Partial<DataTablePropsT<T, C>> & {table: T; columns: C; title?: string};

/**
 * Creates a configuration object for the DataTable component.
 *
 * @template T - The table name key from the Database.
 * @template C - The array of column selections for the specified table.
 * @param {Partial<DataTablePropsT<T, C>> & {table: T; columns: C; title?: string}} config - The configuration object for the DataTable.
 * @param {T} config.table - The name of the table.
 * @param {string} [config.title] - The title of the data table.
 * @param {C} config.columns - The columns to be included in the data table.
 * @param {SelectedColumnsKeysT<T, C>} [config.searchColumn] - The column to be used for search.
 * @param {FilterT[]} [config.filters] - The filters to be applied to the data table.
 * @param {any} [config.restProps] - Any additional properties to be included in the data table configuration.
 * @returns {DataTablePropsT<T, C>} The configuration object for the DataTable component.
 */
export function createDataTableConfig<T extends keyof Database['public']['Tables'], C extends ColumnSelectionT<T>[]>({
  table,
  title,
  columns,
  searchColumn,
  filters,
  ...restProps
}: Partial<DataTablePropsT<T, C>> & {table: T; columns: C; title?: string}): DataTablePropsT<T, C> {
  return {
    table,
    columns,
    title: title || `${table} Data`,
    searchColumn: searchColumn || (`${String(table).toLowerCase()}_name` as SelectedColumnsKeysT<T, C>),
    filters: filters || [],
    columnsDisplay: restProps.columnsDisplay || [],
    actionsDisplay: restProps.actionsDisplay || [],
    ...restProps, // Include any additional props
  };
}
